import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllOrdersOfShop } from "../../redux/actions/order";
import styles from "../../styles/styles";
import { RxCross1 } from "react-icons/rx";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import { loadSeller } from "../../redux/actions/user";
import { AiOutlineDelete, AiOutlineBank, AiOutlineMessage, AiOutlineSend } from "react-icons/ai";
import { SiPaypal } from "react-icons/si";
import { FiDollarSign, FiClock, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import { useCurrency } from "../../context/CurrencyContext";
import socketIO from "socket.io-client";
const ENDPOINT = "http://localhost:4000/";

const WithdrawMoney = () => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { seller } = useSelector((state) => state.seller);
  const { orders } = useSelector((state) => state.order);
  const { formatPrice } = useCurrency();
  const [paymentMethod, setPaymentMethod] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState(1);
  const [paypalEmail, setPaypalEmail] = useState("");
  const [bankInfo, setBankInfo] = useState({
    bankName: "",
    bankCountry: "India",
    bankSwiftCode: null,
    ifscCode: "",
    bankAccountNumber: null,
    bankHolderName: "",
    bankAddress: "",
  });

  // History state
  const [withdrawHistory, setWithdrawHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [socket, setSocket] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const chatScrollRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(getAllOrdersOfShop(seller._id));
    if (seller?.paypalEmail) {
      setPaypalEmail(seller.paypalEmail);
    }
    fetchWithdrawHistory();
    fetchChatHistory();

    const newSocket = socketIO(ENDPOINT, { transports: ["websocket"] });
    setSocket(newSocket);

    newSocket.on("connect", () => {
      newSocket.emit("joinWithdrawReport", { vendorId: seller._id, userType: "vendor" });
    });

    newSocket.on("receiveWithdrawReportMessage", (message) => {
      setChatMessages((prev) => [...prev, message]);
    });

    return () => {
      newSocket.emit("leaveWithdrawReport", { vendorId: seller._id });
      newSocket.disconnect();
    };
  }, [dispatch, seller]);

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const fetchWithdrawHistory = async () => {
    try {
      const { data } = await axios.get(`${server}/withdraw/get-seller-withdraws`, { withCredentials: true });
      if (data.success) {
        setWithdrawHistory(data.withdraws);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchChatHistory = async () => {
    try {
      const { data } = await axios.get(`${server}/withdraw-report/thread/${seller._id}`, { withCredentials: true });
      if (data.success && data.report) {
        setChatMessages(data.report.messages);
        // Mark as read
        await axios.put(`${server}/withdraw-report/mark-read/${seller._id}`, { reader: "vendor" }, { withCredentials: true });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const sendChatMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      const { data } = await axios.post(`${server}/withdraw-report/add-message/${seller._id}`, {
        text: newMessage,
        sender: "vendor"
      }, { withCredentials: true });

      if (data.success) {
        setChatMessages((prev) => [...prev, data.message]);
        socket.emit("sendWithdrawReportMessage", { vendorId: seller._id, message: data.message });
        setNewMessage("");
      }
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const withdrawMethod = {
      bankName: bankInfo.bankName,
      bankCountry: bankInfo.bankCountry,
      bankSwiftCode: bankInfo.bankSwiftCode || bankInfo.ifscCode,
      ifscCode: bankInfo.ifscCode,
      bankAccountNumber: bankInfo.bankAccountNumber,
      bankHolderName: bankInfo.bankHolderName,
      bankAddress: bankInfo.bankAddress,
    };

    setPaymentMethod(false);

    await axios
      .put(
        `${server}/shop/update-payment-methods`,
        {
          withdrawMethod,
        },
        { withCredentials: true }
      )
      .then((res) => {
        toast.success("Withdraw method added successfully!");
        dispatch(loadSeller());
        setBankInfo({
          bankName: "",
          bankCountry: "India",
          bankSwiftCode: null,
          ifscCode: "",
          bankAccountNumber: null,
          bankHolderName: "",
          bankAddress: "",
        });
      })
      .catch((error) => {
        toast.error(error.response?.data?.message || "Something went wrong!");
      });
  };

  const deleteHandler = async () => {
    await axios
      .delete(`${server}/shop/delete-withdraw-method`, {
        withCredentials: true,
      })
      .then((res) => {
        toast.success("Withdraw method deleted successfully!");
        dispatch(loadSeller());
      });
  };

  // Calculate metrics to match DashboardHero
  const totalRevenue = orders ? orders.reduce((acc, curr) => acc + (curr.totalPrice || 0), 0) : 0;
  
  const withdrawnAmount = withdrawHistory
    .filter(w => w.status === "succeed" || w.status === "payout_completed" || w.status === "Paid")
    .reduce((acc, curr) => acc + curr.amount, 0);

  const pendingWithdrawals = withdrawHistory
    .filter(w => w.status === "Processing" || w.status === "Pending" || w.status === "payout_initiated")
    .reduce((acc, curr) => acc + curr.amount, 0);

  const balanceVal = totalRevenue - withdrawnAmount - pendingWithdrawals;
  const availableBalance = balanceVal.toFixed(2);

  const withdrawHandler = async () => {
    const amountVal = Number(withdrawAmount);
    if (amountVal < 1 || amountVal > balanceVal) {
      toast.error("You can't withdraw this amount!");
    } else {
      setIsSubmitting(true);
      const amount = amountVal;
      try {
        await axios.post(
          `${server}/withdraw/create-withdraw-request`,
          { amount },
          { withCredentials: true }
        );
        if (socket) {
          socket.emit("adminNotification", {
            type: "vendor_withdraw",
            message: `New withdraw request of ₹${amountVal}`,
          });
        }
        toast.success("Withdraw money request is successful!");
        fetchWithdrawHistory();
        dispatch(loadSeller());
        setOpen(false);
        setWithdrawAmount(1);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to create withdraw request");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "succeed":
      case "Paid":
      case "payout_completed":
        return "bg-green-100 text-green-800";
      case "Processing":
      case "Pending":
      case "payout_initiated":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
      case "Rejected":
      case "payout_failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="w-full p-4 800px:p-8 bg-gray-50 min-h-screen">
      {/* Top Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">Total Revenue</p>
            <h3 className="text-2xl font-bold text-gray-900">{formatPrice(totalRevenue)}</h3>
          </div>
          <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center">
            <FiDollarSign size={24} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">Available Balance</p>
            <h3 className="text-2xl font-bold text-green-600">{formatPrice(availableBalance)}</h3>
          </div>
          <div className="w-12 h-12 bg-green-50 text-green-600 rounded-full flex items-center justify-center">
            <FiCheckCircle size={24} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">Pending Withdrawals</p>
            <h3 className="text-2xl font-bold text-orange-600">{formatPrice(pendingWithdrawals)}</h3>
          </div>
          <div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-full flex items-center justify-center">
            <FiClock size={24} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">Total Withdrawn</p>
            <h3 className="text-2xl font-bold text-gray-900">{formatPrice(withdrawnAmount)}</h3>
          </div>
          <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center">
            <AiOutlineBank size={24} />
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 mb-8">
        {/* Left Column: Actions */}
        <div className="lg:w-1/2 flex flex-col gap-6">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Withdraw Funds</h3>
              <button
                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => (balanceVal < 1 ? toast.error("Not enough balance") : setOpen(true))}
                disabled={balanceVal < 1}
              >
                Request Withdrawal
              </button>
            </div>
            
            <div className="border-t border-gray-100 pt-6">
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">Configured Payment Methods</h4>
              {seller && seller?.withdrawMethod ? (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <AiOutlineBank className="text-gray-600" size={20} />
                        <span className="font-semibold text-gray-900">{seller.withdrawMethod.bankName}</span>
                      </div>
                      <p className="text-sm text-gray-600">A/C: ****{String(seller.withdrawMethod.bankAccountNumber).slice(-4)}</p>
                    </div>
                    <button onClick={deleteHandler} className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors">
                      <AiOutlineDelete size={20} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <p className="text-gray-500 mb-4">No withdrawal method configured yet.</p>
                  <button onClick={() => { setOpen(true); setPaymentMethod(true); }} className="text-primary-600 font-semibold hover:underline">
                    Add Payment Method
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* History Table (Moved under Actions) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col flex-1 min-h-[400px]">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900">Withdrawal History</h3>
            </div>
            
            {loadingHistory ? (
              <div className="p-8 text-center text-gray-500">Loading history...</div>
            ) : withdrawHistory.length === 0 ? (
              <div className="p-12 text-center text-gray-500 flex-1 flex flex-col items-center justify-center">
                <FiDollarSign size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-lg font-medium text-gray-900">No withdrawals yet</p>
                <p className="mt-1">When you request a withdrawal, it will appear here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto flex-1">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                      <th className="p-4 font-semibold">Date</th>
                      <th className="p-4 font-semibold">Amount</th>
                      <th className="p-4 font-semibold">Status</th>
                      <th className="p-4 font-semibold">Remarks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {withdrawHistory.map((item) => (
                      <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-4 text-sm text-gray-600">
                          {new Date(item.createdAt).toLocaleDateString()} <br/>
                          <span className="text-xs text-gray-400">{new Date(item.createdAt).toLocaleTimeString()}</span>
                        </td>
                        <td className="p-4 text-sm font-bold text-gray-900">
                          {formatPrice(item.amount)}
                        </td>
                        <td className="p-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(item.status)}`}>
                            {item.status === "succeed" ? "Paid" : item.status === "Processing" ? "Pending" : item.status}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-gray-600 max-w-xs">
                          {item.status === "Rejected" && item.rejectionReason ? (
                            <div className="flex items-start space-x-1 text-red-600 bg-red-50 p-2 rounded text-xs">
                              <FiAlertCircle className="mt-0.5 shrink-0" />
                              <span>{item.rejectionReason}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Withdraw Report Chat */}
        <div className="lg:w-1/2 flex flex-col h-[calc(100vh-200px)] min-h-[500px]">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col h-full overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-primary-600 to-indigo-600 text-white flex items-center space-x-2">
              <AiOutlineMessage size={20} />
              <h3 className="font-bold">Withdraw Support</h3>
            </div>
            
            <div className="flex-1 p-4 overflow-y-auto bg-gray-50" ref={chatScrollRef}>
              {chatMessages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-400">
                  <AiOutlineMessage size={48} className="mb-2 opacity-50" />
                  <p>No messages yet. Contact support for withdrawal issues.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {chatMessages.map((msg, index) => (
                    <div key={index} className={`flex ${msg.sender === 'vendor' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-lg p-3 ${
                        msg.sender === 'vendor' 
                          ? 'bg-primary-600 text-white rounded-br-none' 
                          : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none shadow-sm'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                        <p className={`text-[10px] mt-1 text-right ${msg.sender === 'vendor' ? 'text-primary-100' : 'text-gray-400'}`}>
                          {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <form onSubmit={sendChatMessage} className="p-3 border-t border-gray-200 bg-white flex items-center space-x-2">
              <input
                type="text"
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all text-sm"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
              />
              <button 
                type="submit" 
                disabled={!newMessage.trim()}
                className="p-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <AiOutlineSend size={18} />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Withdraw Modals */}
      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900">
                {paymentMethod ? "Add Payment Method" : "Request Withdrawal"}
              </h3>
              <button onClick={() => { setOpen(false); setPaymentMethod(false); }} className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors">
                <RxCross1 size={20} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto">
              {paymentMethod ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h4 className="font-semibold text-gray-900 border-b pb-2">Bank Account Details</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
                    <input type="text" required value={bankInfo.bankName} onChange={(e) => setBankInfo({ ...bankInfo, bankName: e.target.value })} className={styles.input} />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                      <input type="text" required value={bankInfo.bankCountry} onChange={(e) => setBankInfo({ ...bankInfo, bankCountry: e.target.value })} className={styles.input} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code *</label>
                      <input type="text" required value={bankInfo.ifscCode} onChange={(e) => setBankInfo({ ...bankInfo, ifscCode: e.target.value.toUpperCase() })} className={styles.input} maxLength={11} />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number *</label>
                    <input type="number" required value={bankInfo.bankAccountNumber || ""} onChange={(e) => setBankInfo({ ...bankInfo, bankAccountNumber: e.target.value })} className={styles.input} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name *</label>
                    <input type="text" required value={bankInfo.bankHolderName} onChange={(e) => setBankInfo({ ...bankInfo, bankHolderName: e.target.value })} className={styles.input} />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Address *</label>
                    <input type="text" required value={bankInfo.bankAddress} onChange={(e) => setBankInfo({ ...bankInfo, bankAddress: e.target.value })} className={styles.input} />
                  </div>

                  <div className="pt-4">
                    <button type="submit" className="w-full bg-primary-600 text-white font-semibold py-3 rounded-lg hover:bg-primary-700 transition-colors">
                      Save Payment Method
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  {seller && seller?.withdrawMethod ? (
                    <>
                      <div className="bg-green-50 text-green-800 p-4 rounded-lg border border-green-200">
                        <p className="text-sm font-medium">Available to Withdraw</p>
                        <p className="text-3xl font-bold">{formatPrice(availableBalance)}</p>
                      </div>
                      
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Withdrawal Amount</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 font-semibold">₹</span>
                          </div>
                          <input
                            type="number"
                            placeholder="Enter amount"
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all text-lg font-semibold"
                            max={balanceVal}
                            min={1}
                          />
                        </div>
                        <p className="text-xs text-gray-500">Minimum withdrawal amount is ₹1.00</p>
                      </div>

                      <button
                        onClick={withdrawHandler}
                        disabled={Number(withdrawAmount) < 1 || Number(withdrawAmount) > balanceVal || isSubmitting}
                        className="w-full flex items-center justify-center space-x-2 bg-primary-600 text-white font-semibold py-3 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Submitting...</span>
                          </>
                        ) : (
                          <span>Submit Request</span>
                        )}
                      </button>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <AiOutlineBank size={48} className="mx-auto text-gray-400 mb-4" />
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Setup Payment Method</h4>
                      <p className="text-gray-500 mb-6">You need to add a bank account before you can withdraw funds.</p>
                      <button onClick={() => setPaymentMethod(true)} className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors">
                        Add Method Now
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawMoney;
